// @flow
import React from "react"
import { css } from "react-emotion/macro"
import Downshift from "downshift"
import { color } from "../styles/variables"
import { AngleDownIcon } from "./Icons/AngleDownIcon"

type tSelect = {
  items: Array<string>,
  activeItem: string,
  label?: string,
  placeholder: string,
  icon?: Node,
  disabled?: boolean,
  onChange: (any) => void
}

export const Select = ({
  items,
  activeItem,
  icon,
  label,
  onChange,
  placeholder,
  disabled
}: tSelect) => {
  return (
    <Downshift
      itemToString={(i) => (i ? i.name : "")}
      selectedItem={activeItem}
      onChange={onChange}
    >
      {({
        highlightedIndex,
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getItemProps,
        getLabelProps,
        getInputProps
      }) => (
        <div
          className={css`
            .input-wrapper {
              position: relative;
              border-bottom: 1px dashed #9b9b9c;
              background: transparent;
              padding: 0 0 0 15px;
              min-width: 205px;
              transition: border 0.5s ease;
              border-radius: 2px;
              display: flex;
              justify-content: space-between;

              .icon-wrapper {
                position: absolute;
                left: 0;
                bottom: 3px;

                svg {
                  path {
                    fill: #9b9b9c;
                  }
                }
              }

              ${disabled &&
                css`
                  cursor: not-allowed;
                  opacity: 0.5;
                `}

              input {
                font-size: 14px;
                background: transparent;
                line-height: 25px;
              }

              ${isOpen &&
                css`
                  border-bottom-right-radius: 0;
                  border-bottom-left-radius: 0;
                `};

              &:hover,
              &:focus {
                border-bottom: 1px solid ${color.rgb.orange[0]};

                ${disabled &&
                  css`
                    box-shadow: none;
                  `}
              }
            }

            .field-group {
              position: relative;
              display: flex;

              label {
                font-weight: 600;
                text-transform: uppercase;
              }
            }

            input {
              word-wrap: break-word;
              line-height: 38px;
              outline: 0;
              white-space: normal;
              display: inline-block;
              box-shadow: none;
              border: none;
              margin: 0;
            }

            .menu {
              max-height: 20rem;
              overflow-y: auto;
              overflow-x: hidden;
              outline: 0;
              box-shadow: 2px 2px 16px rgba(0, 0, 0, 0.15);
              background-color: ${color.rgb.white[0]};
              border: 1px solid ${color.rgb.gray[2]};
              position: absolute;
              z-index: 100;
              left: 0;
              right: 0;
              margin: auto;

              top: 100%;
              border-bottom-right-radius: 5px;
              border-bottom-left-radius: 5px;
            }

            .control-button {
              background-color: rgba(255, 255, 255, 0);
              border: none;
              cursor: pointer;
              padding: 0 12px 0 10.5px;
              position: absolute;
              width: 100%;
              top: 0;
              left: 0;
              bottom: 0;
              right: 0;
              text-align: right;

              svg {
                width: 14px;

                path {
                  fill: #333333;
                }
              }

              path {
                transition: fill 0.5s ease;
              }

              &:focus {
                outline: 1px solid ${color.rgba.orange(0.15)[0]};
              }

              ${isOpen &&
                css`
                  svg {
                    fill: ${color.rgb.orange[0]};
                  }
                `};
            }
          `}
        >
          <div className="field-group">
            <div className="input-wrapper">
              <span
                className={css`
                  display: flex;
                  align-items: center;
                `}
              >
                {label && (
                  <label {...getLabelProps()}>
                    {label}
                    &nbsp;
                  </label>
                )}
                {icon ? <span className="icon-wrapper">{icon}</span> : null}
                <input
                  disabled
                  {...getInputProps()}
                  value={selectedItem || placeholder || ""}
                />
              </span>
              {!disabled && (
                <button
                  className="control-button"
                  {...getToggleButtonProps({})}
                >
                  <AngleDownIcon />
                </button>
              )}
              {isOpen && !disabled && (
                <div className="menu">
                  {items.map((item, index) => {
                    const isActive = highlightedIndex === index
                    const isSelected = selectedItem === item
                    return (
                      <div
                        key={index}
                        className={css`
                          position: relative;
                          cursor: pointer;
                          display: block;
                          border: none;
                          height: auto;
                          text-align: left;
                          line-height: 1em;
                          text-transform: none;
                          font-weight: 400;
                          box-shadow: none;
                          padding: 0.8rem 1.1rem;
                          white-space: normal;
                          word-wrap: normal;
                          transition: all 0.5s ease;

                          ${isSelected &&
                            css`
                              background: ${color.rgba.orange(0.15)[0]};
                              font-weight: 700;
                            `};

                          ${isActive &&
                            css`
                              color: ${color.rgb.white[0]};
                              background: ${color.rgb.orange[0]};
                            `}
                        `}
                        {...getItemProps({ item })}
                      >
                        {" "}
                        {item}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Downshift>
  )
}



// WEBPACK FOOTER //
// /opt/build/repo/src/components/Select.js